import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext.js';
import { useToast } from '../context/ToastContext.js';
import { checkOnlineStatus } from '../firebase.js';
import {
  createSubscription,
  cancelSubscription,
  isUserSubscribed,
  getStreamerSubscriptions,
  getUserSubscriptions,
  getSubscriptionTiers
} from '../services/subscriptionService.js';

/**
 * Custom hook to manage subscriptions
 * @param {string} streamerId - Optional streamer ID to check subscription for
 * @returns {Object} - Subscription functions and data
 */
export const useSubscription = (streamerId = null) => {
  const { currentUser } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [streamerSubscriptions, setStreamerSubscriptions] = useState([]);
  const [userSubscriptions, setUserSubscriptions] = useState([]);
  const [subscriptionTiers] = useState(getSubscriptionTiers());
  const [isOffline, setIsOffline] = useState(!checkOnlineStatus());

  // Monitor online status
  useEffect(() => {
    const handleOnlineStatus = () => {
      setIsOffline(!navigator.onLine);
    };
    
    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOnlineStatus);
    
    return () => {
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOnlineStatus);
    };
  }, []);

  // Check if the current user is subscribed to the streamer
  useEffect(() => {
    const checkSubscription = async () => {
      if (!currentUser || !streamerId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        
        if (isOffline) {
          console.log('Offline: Unable to check subscription status');
          return;
        }
        
        const subscribed = await isUserSubscribed(currentUser.uid, streamerId);
        setIsSubscribed(subscribed);
      } catch (err) {
        console.error('Error checking subscription:', err);
        setError('Failed to check subscription status');
      } finally {
        setLoading(false);
      }
    };

    checkSubscription();
  }, [currentUser, streamerId, isOffline]);

  // Get streamer's subscribers
  const fetchStreamerSubscriptions = useCallback(async () => {
    if (!streamerId || isOffline) return;

    try {
      setLoading(true);
      const subscriptions = await getStreamerSubscriptions(streamerId);
      setStreamerSubscriptions(subscriptions);
    } catch (err) {
      console.error('Error fetching streamer subscriptions:', err);
      setError('Failed to fetch subscribers');
      showToast('Failed to load subscribers', 'error');
    } finally {
      setLoading(false);
    }
  }, [streamerId, showToast, isOffline]);

  // Get user's subscriptions
  const fetchUserSubscriptions = useCallback(async () => {
    if (!currentUser || isOffline) return;

    try {
      setLoading(true);
      const subscriptions = await getUserSubscriptions(currentUser.uid);
      setUserSubscriptions(subscriptions);
    } catch (err) {
      console.error('Error fetching user subscriptions:', err);
      setError('Failed to fetch subscriptions');
      showToast('Failed to load your subscriptions', 'error');
    } finally {
      setLoading(false);
    }
  }, [currentUser, showToast, isOffline]);

  // Subscribe to a streamer
  const subscribe = useCallback(async (tier, streamerData) => {
    if (isOffline) {
      showToast('Cannot subscribe while offline', 'error');
      return null;
    }
    
    if (!currentUser) {
      showToast('You must be logged in to subscribe', 'error');
      return null;
    }

    if (!streamerId) {
      showToast('Invalid streamer information', 'error');
      return null;
    }

    try {
      setLoading(true);
      const tierInfo = subscriptionTiers[tier];
      
      if (!tierInfo) {
        throw new Error('Invalid subscription tier');
      }

      const subscription = await createSubscription(
        currentUser.uid,
        streamerId,
        tier,
        tierInfo.price,
        streamerData
      );

      setIsSubscribed(true);
      showToast(`Successfully subscribed to ${streamerData.displayName || streamerData.username}!`, 'success');
      
      // Refresh data
      await fetchStreamerSubscriptions();
      await fetchUserSubscriptions();
      
      return subscription;
    } catch (err) {
      console.error('Error subscribing:', err);
      setError('Failed to subscribe');
      showToast('Failed to complete subscription', 'error');
      return null;
    } finally {
      setLoading(false);
    }
  }, [currentUser, streamerId, subscriptionTiers, showToast, fetchStreamerSubscriptions, fetchUserSubscriptions, isOffline]);

  // Unsubscribe from a streamer
  const unsubscribe = useCallback(async (subscriptionId) => {
    if (isOffline) {
      showToast('Cannot unsubscribe while offline', 'error');
      return false;
    }
    
    if (!currentUser) {
      showToast('You must be logged in to manage subscriptions', 'error');
      return false;
    }

    try {
      setLoading(true);
      await cancelSubscription(subscriptionId);
      
      setIsSubscribed(false);
      showToast('Subscription canceled successfully', 'success');
      
      // Refresh data
      await fetchStreamerSubscriptions();
      await fetchUserSubscriptions();
      
      return true;
    } catch (err) {
      console.error('Error unsubscribing:', err);
      setError('Failed to cancel subscription');
      showToast('Failed to cancel subscription', 'error');
      return false;
    } finally {
      setLoading(false);
    }
  }, [currentUser, showToast, fetchStreamerSubscriptions, fetchUserSubscriptions, isOffline]);

  // Get subscription stats for a streamer
  const getSubscriptionStats = useCallback(() => {
    if (!streamerSubscriptions.length) return null;

    const totalSubscribers = streamerSubscriptions.length;
    
    // Count subscriptions per tier
    const tierCounts = streamerSubscriptions.reduce((acc, sub) => {
      acc[sub.tier] = (acc[sub.tier] || 0) + 1;
      return acc;
    }, {});
    
    // Calculate total revenue and platform fees
    const revenue = {
      total: 0,
      streamer: 0,
      platform: 0
    };

    streamerSubscriptions.forEach(sub => {
      revenue.total += sub.price;
      revenue.streamer += sub.streamerAmount;
      revenue.platform += sub.platformAmount;
    });

    return {
      totalSubscribers,
      tierCounts,
      revenue
    };
  }, [streamerSubscriptions]);

  return {
    loading,
    error,
    isSubscribed,
    streamerSubscriptions,
    userSubscriptions,
    subscriptionTiers,
    subscribe,
    unsubscribe,
    fetchStreamerSubscriptions,
    fetchUserSubscriptions,
    getSubscriptionStats,
    isOffline
  };
}; 